import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  LayoutComponent as TcLayoutComponent,
  NavigationService,
  PageTitleService,
  BadgeComponent,
  ThemeToggleComponent,
  type NavigationItem,
  type ShellUser,
} from '@techcrowd/ui';
import { ColorSchemeService } from '@techcrowd/tokens';
import { ToastContainerComponent } from '../toast/toast-container.component';
import { AuthService } from '../../../core/services/auth.service';
import { APP_VERSION } from '../../../../version';

const NAV_ITEMS: NavigationItem[] = [
  { label: 'Dashboard', route: '/dashboard', icon: 'ri-dashboard-line' },
  { label: 'Ceníky', route: '/price-lists', icon: 'ri-price-tag-3-line' },
  { label: 'Transakce', route: '/transactions', icon: 'ri-arrow-down-line' },
  { label: 'Billing', route: '/billing', icon: 'ri-file-text-line' },
  { label: 'Reporting', route: '/reporting', icon: 'ri-bar-chart-box-line' },
  { label: 'Nastavení', route: '/settings', icon: 'ri-settings-3-line' },
];

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/price-lists': 'Ceníky',
  '/transactions': 'Transakce',
  '/billing': 'Billing',
  '/reporting': 'Reporting',
  '/settings': 'Nastavení',
  '/tc-ui-demo': 'ds-core demo',
};

@Component({
  selector: 'app-layout',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TcLayoutComponent,
    ToastContainerComponent,
    BadgeComponent,
    ThemeToggleComponent,
  ],
  templateUrl: './layout.component.html',
})
export class LayoutComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly navigation = inject(NavigationService);
  private readonly pageTitle = inject(PageTitleService);
  private readonly colorScheme = inject(ColorSchemeService);
  private readonly destroyRef = inject(DestroyRef);

  readonly brandLabel = 'Billing';
  readonly versionLabel = `v${APP_VERSION}`;
  readonly logoUrl = computed(() =>
    this.colorScheme.isDark() ? 'logos/brinks_lg_wh.png' : 'logos/brinks_lg.png',
  );

  readonly shellUser = computed<ShellUser | null>(() => {
    const login = this.auth.currentLogin();
    if (!login) return null;
    return { displayName: login };
  });

  constructor() {
    this.navigation.setItems(NAV_ITEMS);
    this.syncPageTitle(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.syncPageTitle(event.urlAfterRedirects));
  }

  logout(): void {
    this.auth.logout();
    void this.router.navigate(['/login']);
  }

  private syncPageTitle(url: string): void {
    const path = url.split('?')[0] ?? url;
    const match = Object.keys(PAGE_TITLES)
      .sort((a, b) => b.length - a.length)
      .find((route) => path === route || path.startsWith(`${route}/`));
    this.pageTitle.setTitle(match ? PAGE_TITLES[match] : '');
  }
}
